import { getChatCompletion } from './openaiApiHandler'

/**
 * Generate summaries of student's performance for parents
 * @param {string} question - The question text or latex
 * @param {Array} attempts - Array of attempt objects with attemptNumber, answer, and feedback
 * @returns {Promise<Object>} Object with answerSummary and shortSummary
 */
export const generateParentSummaries = async (question, attempts) => {
  try {
    // Build a summary of the student's attempts
    const attemptsSummary = attempts.map((attempt) => {
      return `Attempt ${attempt.attemptNumber}: ${attempt.feedback.isCorrect ? 'Correct' : 'Incorrect'}\nStudent Answer: ${attempt.answer}\nFeedback: ${attempt.feedback.message}`
    }).join('\n\n')

    const messages = [
      {
        role: "system",
        content: "You are an educational assistant helping parents understand their child's math work. Generate two summaries of the student's performance. Return ONLY a JSON object with 'answerSummary' (4-6 sentences describing what the student did across attempts, their approach, mistakes, and progress) and 'shortSummary' (1-2 sentences condensing the key points). Be encouraging and constructive. Do not include any other text or explanation."
      },
      {
        role: "user",
        content: `Question: ${question}

Student's Attempts:
${attemptsSummary}

Provide summaries as a JSON object with 'answerSummary' and 'shortSummary' fields.`
      }
    ]

    const response = await getChatCompletion(messages, 'gpt-4', 0.3)

    // Parse the JSON response
    const summaries = JSON.parse(response.trim())

    // Validate the response
    if (!summaries.answerSummary || !summaries.shortSummary) {
      throw new Error('Invalid summaries format')
    }

    return summaries
  } catch (error) {
    console.error('Error generating parent summaries:', error)
    return {
      answerSummary: '',
      shortSummary: ''
    }
  }
}

/**
 * Get video recommendations from YouTube based on a question and student attempts
 * @param {string} question - The question text or latex
 * @param {Array} attempts - Array of attempt objects with attemptNumber, answer, and feedback
 * @returns {Promise<Array>} Array of video recommendations with title, url, and thumbnail
 */
export const getVideoRecommendations = async (questionLatex, allAttempts, quota = null) => {
  try {
    // Check if YouTube API key is configured
    const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY

    if (!YOUTUBE_API_KEY || YOUTUBE_API_KEY.trim() === '') {
      console.warn('YouTube API key not configured, skipping video recommendations')
      return []
    }

    // Generate search topics using OpenAI
    const messages = [
      {
        role: "system",
        content: `You are an AI assistant tasked with identifying key topics from a math question and student attempts. Based on the question and attempts, generate 3 specific search queries that would help find educational videos on YouTube. Each query should focus on a specific concept or technique needed to solve this type of problem.

Format your response as a JSON array of strings, like this:
["topic 1", "topic 2", "topic 3"]`
      },
      {
        role: "user",
        content: `Question: ${questionLatex || ''}
Student's Attempts:
${allAttempts.map((attempt, index) => `Attempt ${index + 1}:
  Answer: ${attempt.answer.latex || attempt.answer.text}
  Feedback: ${attempt.feedback.message}`).join('\n\n')}

Generate 3 search queries for educational videos.`
      }
    ]

    const aiResponse = await getChatCompletion(messages, 'gpt-4', 0.1, quota)
    const searchQueries = JSON.parse(aiResponse)

    // Fetch videos for each topic
    const videoRecommendations = []

    for (const query of searchQueries) {
      try {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&maxResults=1&q=${encodeURIComponent(query)}&type=video&key=${YOUTUBE_API_KEY}`
        )

        if (!response.ok) {
          const errorData = await response.json().catch(() => null)
          
          // If API key is invalid, stop trying and return empty results
          if (errorData?.error?.details?.some(d => d.reason === 'API_KEY_INVALID')) {
            console.warn('YouTube API key is invalid. Please configure a valid API key in Secrets.')
            return []
          }
          
          console.error(`YouTube API error for query "${query}":`, response.status, response.statusText, errorData)
          continue
        }

        const data = await response.json()

        if (data.items && data.items.length > 0) {
          const video = data.items[0]
          videoRecommendations.push({
            title: video.snippet.title,
            url: `https://www.youtube.com/watch?v=${video.id.videoId}`,
            thumbnail: video.snippet.thumbnails.default.url
          })
        }
      } catch (fetchError) {
        console.error(`Error fetching video for query "${query}":`, fetchError)
        // Continue with next query
      }
    }

    return videoRecommendations

  } catch (error) {
    console.error('Error generating video recommendations:', error)
    return []
  }
}